import type { Plugin, PluginListenerHandle } from '@capacitor/core';

export interface QToolDevice {
  name: string;
  address: string;
}

export interface ReadoutDeviceResult {
  serialNumber: string;
  type: string;
  rssi: number | null;
  timestamp: number | null;
}

export interface DiscoveredGateway {
  serialNumber: string;
  rssi: number;
  nrssi: number;
}

export type QToolMode =
  | { type: 'idle' }
  | { type: 'qtoolDiscovery' }
  | { type: 'connecting'; qtool: QToolDevice }
  | { type: 'connected'; qtool: QToolDevice }
  | { type: 'firmwareRequired'; qtool: QToolDevice }
  | { type: 'qtoolReadout'; qtool: QToolDevice }
  | { type: 'gatewayDiscovery'; qtool: QToolDevice }
  | { type: 'gatewayReadout'; qtool: QToolDevice; serialNumber: string }
  | { type: 'firmwareUpdate'; qtool: QToolDevice };

const CONNECTED_MODE_TYPES: ReadonlyArray<QToolMode['type']> = [
  'connected',
  'firmwareRequired',
  'qtoolReadout',
  'gatewayDiscovery',
  'gatewayReadout',
  'firmwareUpdate',
];

export function isConnectedMode(mode: QToolMode): boolean {
  return CONNECTED_MODE_TYPES.includes(mode.type);
}

export type QToolReadoutState = 'idle' | 'loading' | 'error';

export type QToolReadoutErrorReason =
  | 'NOT_CONNECTED'
  | 'SDK_FAILURE'
  | 'UNKNOWN';

export type QToolFirmwareUpdateState =
  | 'notRequired'
  | 'available'
  | 'preparing'
  | 'progress'
  | 'complete'
  | 'error';

/**
 * Mirrors the Kotlin `FirmwareUpdateEvent.Error.Reason` names verbatim — the
 * bridge serialises `reason.name`, and both sides pin the list with tests.
 *
 * All reasons except `UPDATE_NOT_POSSIBLE` are transient: offer a retry.
 */
export type QToolFirmwareUpdateErrorReason =
  | 'PREPARE_FAILED'
  | 'BLOCK_FAILED'
  | 'UPDATE_FAILED'
  /** The Q-Tool cannot take this image. Terminal — do NOT offer a retry. */
  | 'UPDATE_NOT_POSSIBLE'
  /**
   * The native watchdog aborted the update because the Qundis SDK went silent
   * without reporting success or failure — typically a dropped BLE link during
   * the block transfer, or a flash/reboot that never came back after the last
   * block. Transient: offer a retry (PHX-2407).
   */
  | 'STALLED'
  /**
   * The native watchdog aborted the update because it never finished inside
   * its overall budget while still reporting progress. The Q-Tool was
   * answering the whole time, so copy must not blame a lost connection.
   * Transient: offer a retry (PHX-2407).
   */
  | 'DEADLINE_EXCEEDED'
  | 'UNKNOWN';

export interface QToolFirmwareUpdateEvent {
  state: QToolFirmwareUpdateState;
  currentVersion?: string;
  targetVersion?: string;
  /**
   * Transfer progress, 0–99. Never reaches 100: the SDK emits its last
   * BLOCK_TRANSMITTED before incrementing the counter and never announces the
   * final partial chunk. Completion is the `complete` state — never a
   * percentage, and `preparing` arrives *after* `progress` while the Q-Tool
   * verifies, flashes and reboots.
   */
  percent?: number;
  blocksTransmitted?: number;
  totalBlocks?: number;
  reason?: QToolFirmwareUpdateErrorReason;
  detail?: string;
}

export interface QToolDiscoveryEvent {
  qtools: QToolDevice[];
}

export interface QToolReadoutStateEvent {
  state: QToolReadoutState;
  reason?: QToolReadoutErrorReason;
}

export interface QToolBluetoothStateEvent {
  enabled: boolean;
}

export interface QToolPlugin extends Plugin {
  startQToolDiscovery(): Promise<void>;
  stopQToolDiscovery(): Promise<void>;
  connect(options: { address: string }): Promise<void>;
  disconnect(): Promise<void>;
  startQToolReadout(): Promise<void>;
  startGatewayDiscovery(): Promise<void>;
  startGatewayReadout(options: {
    serialNumber: string;
    aesKey: string;
  }): Promise<void>;
  stopReadout(): Promise<void>;
  /**
   * Starts — or restarts — the firmware update. Accepted while an update is
   * already on the error screen: the ended run is torn down and the transfer
   * restarts from block 0, which is what backs the "Erneut versuchen" button
   * (PHX-2407). While a run is still live the native side drops the command,
   * so a double-tap cannot restart a healthy transfer — but callers must not
   * invoke it speculatively either.
   */
  startFirmwareUpdate(): Promise<void>;
  syncMode(): Promise<void>;
  getBluetoothState(): Promise<QToolBluetoothStateEvent>;

  addListener(
    eventName: 'qtoolDiscoveryResult',
    listenerFunc: (data: QToolDiscoveryEvent) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolDiscoveryStopped',
    listenerFunc: () => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolModeChanged',
    listenerFunc: (data: QToolMode) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolReadoutDevice',
    listenerFunc: (data: ReadoutDeviceResult) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolReadoutStateChanged',
    listenerFunc: (data: QToolReadoutStateEvent) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolGatewayDiscovered',
    listenerFunc: (data: DiscoveredGateway) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolGatewayDiscoveryError',
    listenerFunc: (data: { error: string }) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolGatewayDeviceFound',
    listenerFunc: (data: ReadoutDeviceResult) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolGatewayReadoutStateChanged',
    listenerFunc: (data: {
      state: 'connected' | 'done' | 'error';
      reason?:
        | 'BEACON_TIMEOUT'
        | 'INVALID_AES_KEY'
        | 'INVALID_SERIAL'
        | 'SCAN_FAILED'
        | 'UNKNOWN';
    }) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolFirmwareUpdate',
    listenerFunc: (data: QToolFirmwareUpdateEvent) => void
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'qtoolBluetoothStateChanged',
    listenerFunc: (data: QToolBluetoothStateEvent) => void
  ): Promise<PluginListenerHandle>;
}
